import { AppLayout } from '@/components/AppLayout';
import { Card } from '@/components/ui/card';
import { StageBadge } from '@/components/StageBadge';
import { ScoreBadge } from '@/components/ScoreBadge';
import { useDashboardStats, useRecentCandidates, useTopPerformingJobs } from '@/hooks/useDashboard';
import { usePlanUsage } from '@/hooks/usePlanUsage';
import { useTrialStatus, isTrialActive, isSubscribedDuringTrial, hasTrialExtras, getEffectiveRenewalDate } from '@/hooks/useTrialStatus';
import { TrialBanner } from '@/components/trial/TrialBanner';

import { Briefcase, Users, Video, FileCheck, ArrowRight, Plus, TrendingUp, TrendingDown, Clock, MapPin, Info, UserRound } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { detectTextDirection } from '@/lib/textDirection';
import { formatLocalizedDate, formatRelativeTime } from '@/lib/localizedDate';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING, HIRE_STAT_GRID, HIRE_DASHBOARD_GRID, HIRE_PAGE_HEADER_PAD } from '@/lib/hireResponsive';
import { RESPONSIVE_ACTION_ROW, RESPONSIVE_BUTTON_TEXT, RESPONSIVE_HEADER_ROW, SAFE_COMPACT_VALUE, SAFE_TEXT_WRAP } from '@/lib/responsiveText';

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentApps, isLoading: recentLoading } = useRecentCandidates();
  const { data: topJobs } = useTopPerformingJobs();
  const { planName, items: planUsageItems, isLoading: planUsageLoading } = usePlanUsage();
  const { data: trialStatus } = useTrialStatus();
  const { profile } = useAuth();

  const firstName = profile?.full_name?.split(' ')[0] || t('hire.dashboard.defaultName');
  const today = formatLocalizedDate(new Date(), 'PPPP', language);

  const statCards = [
    { label: t('hire.dashboard.totalJobsCreated'), value: stats?.totalJobsCreated ?? 0, icon: Briefcase, iconBg: 'bg-primary/10', iconColor: 'text-primary', suffix: '', trend: stats?.jobsTrend },
    { label: t('hire.dashboard.totalApplications'), value: stats?.totalApplications ?? 0, icon: FileCheck, iconBg: 'bg-warning/10', iconColor: 'text-warning', suffix: '', trend: stats?.applicationsTrend },
    { label: t('hire.dashboard.totalInterviews'), value: stats?.totalInterviewsConducted ?? 0, icon: Video, iconBg: 'bg-brand-violet/10', iconColor: 'text-brand-violet', suffix: '', trend: stats?.interviewsTrend },
    { label: t('hire.dashboard.applicationToHire'), value: stats?.hireRate ?? 0, icon: TrendingUp, iconBg: 'bg-success/10', iconColor: 'text-success', suffix: '%', tooltip: t('hire.dashboard.applicationToHireTooltip'), trend: stats?.hireRateTrend },
  ];

  const usageColorMap: Record<string, string> = {
    job_management: 'bg-primary',
    ai_cv_scoring: 'bg-success',
    ai_interviews: 'bg-warning',
    team_management: 'bg-primary',
    analytics: 'bg-brand-violet',
    candidate_pipeline: 'bg-accent',
    talent_pool: 'bg-success',
    ai_job_generator: 'bg-warning',
  };

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        {/* Welcome Header + Stats — unified card */}
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className={`${HIRE_PAGE_HEADER_PAD} ${RESPONSIVE_HEADER_ROW}`}>
            <div className="min-w-0 max-w-3xl">
              <h1 className={`text-base sm:text-2xl lg:text-3xl font-bold font-display text-foreground ${SAFE_TEXT_WRAP}`}>
                {t('hire.dashboard.welcome', { name: firstName })}
              </h1>
              <p className={`text-muted-foreground mt-0.5 sm:mt-1.5 text-xs sm:text-sm ${SAFE_TEXT_WRAP}`}>{today}</p>
            </div>
            <div className={RESPONSIVE_ACTION_ROW}>
              <Button asChild variant="outline" size="sm" className={`rounded-full px-3 sm:px-5 press-scale text-xs sm:text-sm font-medium ${RESPONSIVE_BUTTON_TEXT}`}>
                <Link to="/analytics">
                  {t('hire.dashboard.viewAnalytics')}
                </Link>
              </Button>
              <Button asChild size="sm" className={`rounded-full px-3 sm:px-5 press-scale text-xs sm:text-sm font-medium ${RESPONSIVE_BUTTON_TEXT}`}>
                <Link to="/jobs">
                  <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-1" /> {t('hire.dashboard.createJob')}
                </Link>
              </Button>
            </div>
          </div>

          {/* Stats Grid inside the welcome card */}
          <div className={`${HIRE_STAT_GRID} px-3.5 sm:px-6 lg:px-8 3xl:px-10 pb-3.5 sm:pb-6 lg:pb-8 3xl:pb-10`}>
            {statCards.map((stat) => (
              <div key={stat.label} className="bg-muted/40 border border-border rounded-xl sm:rounded-2xl p-2.5 sm:p-4 hover-lift cursor-pointer transition-colors relative">
                {stat.trend != null && (
                  <span className={`absolute top-1.5 sm:top-3 end-1.5 sm:end-3 inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-px sm:py-0.5 rounded-full ${stat.trend > 0 ? 'bg-success/10 text-success' : stat.trend < 0 ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                    {stat.trend > 0 ? <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : stat.trend < 0 ? <TrendingDown className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
                    {stat.trend > 0 ? '+' : ''}{stat.trend}%
                  </span>
                )}
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl ${stat.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {statsLoading ? (
                      <Skeleton className="h-5 sm:h-8 w-10 sm:w-12" />
                    ) : (
                      <p className="text-lg sm:text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums leading-tight">
                        {stat.value.toLocaleString()}{stat.suffix}
                      </p>
                    )}
                    <div className="flex items-center gap-0.5 sm:gap-1 mt-0.5 sm:mt-1">
                      <p className={`text-[10px] sm:text-sm text-muted-foreground font-medium ${SAFE_COMPACT_VALUE}`}>{stat.label}</p>
                      {stat.tooltip && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground/60 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                              {stat.tooltip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Trial Banner */}
        <TrialBanner />

        {/* Bottom Section: Recent Applications + Right Column */}
        <div className={HIRE_DASHBOARD_GRID}>
          {/* Recent Applications — 3/5 with purple left accent */}
          <Card className="lg:col-span-3 shadow-card border-border overflow-hidden flex flex-col border-s-4 border-s-primary">
            <div className="p-3.5 sm:p-5 border-b border-border flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="font-display font-semibold text-foreground text-sm sm:text-lg">{t('hire.dashboard.recentApplications')}</h2>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">{t('hire.dashboard.latestSubmissions')}</p>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-primary hover:text-primary h-7 sm:h-9 px-2 sm:px-3">
                <Link to="/candidates" className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-medium">
                  {t('common.viewAll')} <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 rtl-flip" />
                </Link>
              </Button>
            </div>
            {recentLoading ? (
              <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 sm:h-16 w-full rounded-xl" />)}</div>
            ) : !recentApps?.length ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 text-center">
                <Users className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground/30 mx-auto mb-2 sm:mb-3" />
                <p className="text-xs sm:text-sm font-medium text-foreground mb-1">{t('hire.dashboard.noCandidatesYet')}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mb-3 sm:mb-4">{t('hire.dashboard.postJobToStart')}</p>
                <Button asChild variant="outline" size="sm" className="rounded-xl h-7 sm:h-9 text-xs sm:text-sm">
                  <Link to="/jobs">{t('hire.dashboard.postAJob')}</Link>
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {recentApps.map((app) => (
                  <Link
                    key={app.id}
                    to={`/candidates/${app.candidate_id}?app=${app.id}`}
                    className="flex items-center gap-2.5 sm:gap-4 px-3.5 sm:px-5 py-2.5 sm:py-4 hover:bg-muted/40 transition-colors group"
                  >
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl border border-border flex items-center justify-center flex-shrink-0 ${
                      app.candidates?.source === 'seek_ai' ? 'bg-success/10' : 'bg-muted/60'
                    }`}>
                      <UserRound className={`w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 ${
                        app.candidates?.source === 'seek_ai' ? 'text-success' : 'text-primary'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-xs sm:text-sm text-foreground truncate" dir={detectTextDirection(app.candidates?.name)}>{app.candidates?.name}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground truncate" dir={detectTextDirection(app.jobs?.title)}>{app.jobs?.title}</p>
                    </div>
                    <div className="w-16 flex-shrink-0 flex justify-center">
                      {app.cv_scores?.total_score != null && (
                        <ScoreBadge score={app.cv_scores.total_score} size="sm" />
                      )}
                    </div>
                    <div className="hidden sm:flex w-28 flex-shrink-0 justify-center">
                      <StageBadge stage={app.stage} />
                    </div>
                    <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-16 flex-shrink-0 justify-end">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(app.created_at, language)}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors flex-shrink-0 rtl-flip" />
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-6">
            {/* Plan Usage — purple left accent */}
            <Card className="shadow-card border-border p-3 sm:p-5 space-y-3 sm:space-y-5 border-s-4 border-s-primary">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-display font-semibold text-foreground text-sm sm:text-base">{t('hire.dashboard.planUsage')}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isTrialActive(trialStatus) ? t('hire.dashboard.trialUsageLimits') : t('hire.dashboard.monthlyLimits')}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {isTrialActive(trialStatus) && (
                    <Badge variant="outline" className="text-[10px] border-warning text-warning">
                      {t('hire.dashboard.trial')}
                    </Badge>
                  )}
                  {hasTrialExtras(trialStatus) && (
                    <Badge variant="outline" className="text-[10px] border-success text-success">
                      {t('hire.dashboard.bonus')}
                    </Badge>
                  )}
                  <span className="text-[10px] sm:text-[11px] font-bold px-2 sm:px-2.5 py-1 rounded-full bg-primary/10 text-primary uppercase tracking-wide">
                    {planName || (isTrialActive(trialStatus) ? t('hire.dashboard.trial') : t('hire.dashboard.freePlan'))}
                  </span>
                </div>
              </div>
              {planUsageLoading ? (
                <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-8 w-full rounded" />)}</div>
              ) : planUsageItems.map((item) => {
                const isUnlimited = item.cap === null || item.limitType === 'unlimited';
                const pct = isUnlimited ? 0 : item.cap! > 0 ? Math.min((item.used / item.cap!) * 100, 100) : 0;
                const color = usageColorMap[item.label] || 'bg-primary';
                const periodHint = item.limitType === 'active'
                  ? t('hire.dashboard.periodActive')
                  : item.limitType === 'monthly'
                    ? t('hire.dashboard.periodThisMonth')
                    : item.limitType === 'total'
                      ? t('hire.dashboard.periodLifetime')
                      : '';
                return (
                  <div key={item.serviceKey} className="space-y-1.5 sm:space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="font-medium text-foreground">
                        {item.serviceName || item.serviceKey}
                        {periodHint && <span className="ms-1 text-[10px] text-muted-foreground font-normal">{periodHint}</span>}
                        {item.isTrial && <span className="ms-1 text-[10px] text-warning font-bold">{t('hire.dashboard.periodTrial')}</span>}
                        {item.isUsingCredits && <span className="ms-1 text-[10px] text-brand-violet font-bold">{t('hire.dashboard.periodCredits')}</span>}
                      </span>
                      <span className="font-semibold tabular-nums text-muted-foreground">
                        {item.used}{isUnlimited ? ' / ∞' : ` / ${item.cap}`}
                        {item.creditBalance > 0 && <span className="text-primary font-medium"> +{item.creditBalance}</span>}
                      </span>
                    </div>
                    <div className="h-1.5 sm:h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${pct >= 90 ? 'bg-destructive' : pct >= 75 ? 'bg-warning' : color} transition-all duration-500`}
                        style={{ width: isUnlimited ? '0%' : `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </Card>

            {/* Top Performing Jobs — purple left accent */}
            <Card className="shadow-card border-border p-4 sm:p-5 space-y-3 sm:space-y-4 border-s-4 border-s-primary">
              <div>
                <h2 className="font-display font-semibold text-foreground text-base">{t('hire.dashboard.topPerformingJobs')}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{t('hire.dashboard.mostApplications')}</p>
              </div>
              {(!topJobs || topJobs.length === 0) ? (
                <div className="py-6 text-center">
                  <Briefcase className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{t('hire.dashboard.noActiveJobsYet')}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {topJobs.map((job, idx) => (
                    <Link
                      key={job.id}
                      to={`/jobs/${job.id}`}
                      className="flex items-center gap-3 group hover:bg-muted/40 -mx-2 px-2 py-2.5 rounded-xl transition-colors"
                    >
                      <span className="w-6 text-sm font-bold text-primary text-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate" dir={detectTextDirection(job.title)}>{job.title}</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {job.department && <span>{job.department}</span>}
                          {job.department && job.location && <span>•</span>}
                          {job.location && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="w-3 h-3" /> {job.location}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-bold tabular-nums text-foreground bg-muted px-2.5 py-1 rounded-lg">
                        {job.applicationCount}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
